import { Box, Button, Divider, Grid2, Typography } from '@mui/material';
import React, { useEffect, useMemo } from 'react';
import { Add, Check, Remove } from '@mui/icons-material';
import { Field, useForm } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import JsxParser from 'react-jsx-parser';
import { formatNumber, getTicketCardPriceView, resolvePriceDisplayMode } from '../helper';
import EarlyBirdBanner from './EarlyBirdBanner';
import ExternalLink from '../../../components/ExternalLink';

// ─── PriceDisplay ─────────────────────────────────────────────────────────────
// Renders the price block on the right side of each ticket card.
//
// Layout for mode === 'both':
//
//   ~~594,99 €~~          ← original gross (red strikethrough, small)
//   475,99 €              ← sale gross (large, prominent)
//   inkl. MwSt.           ← gross label (muted caption)
//   ─────────────────     ← thin divider
//   ~~499,99 €~~ netto    ← original net with label inline (muted, small)
//   399,99 € netto        ← sale net (muted, caption)
//
// Layout for mode === 'gross' or 'net' (single price):
//
//   ~~original~~          ← only if showSale
//   price                 ← prominent
//   inkl./zzgl. MwSt.    ← label

function PriceDisplay({
  priceView,
  showVatNotice,
  vatNoticeShort,
  vatGrossLabel,
  vatNetLabel,
  textAlign = 'right',
}) {
  if (priceView.mode === 'both') {
    const { gross, net } = priceView;
    const grossFinal = formatNumber(gross.salePrice ?? gross.price);
    const netFinal = formatNumber(net.salePrice ?? net.price);

    return (
      <Box sx={{ width: '100%', textAlign }}>
        {/* ── Gross block ── */}
        {gross.showSale && (
          <Typography
            variant="body2"
            sx={{ color: 'error.main', textDecoration: 'line-through', lineHeight: 1.3 }}
          >
            {formatNumber(gross.price)}
          </Typography>
        )}
        <Typography variant="h6" sx={{ lineHeight: 1.2, fontWeight: 700 }}>
          {grossFinal}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mb: 0.75 }}>
          {vatGrossLabel}
        </Typography>

        {/* ── Separator ── */}
        <Box
          sx={{
            borderTop: '1px solid',
            borderColor: 'divider',
            mb: 0.75,
            ml: 'auto',
            width: '60%',
          }}
        />

        {/* ── Net block ── */}
        {net.showSale && (
          <Typography
            variant="caption"
            sx={{
              color: 'text.disabled',
              textDecoration: 'line-through',
              display: 'block',
              lineHeight: 1.3,
            }}
          >
            {formatNumber(net.price)}{' '}
            <Box component="span" sx={{ textDecoration: 'none' }}>{vatNetLabel}</Box>
          </Typography>
        )}
        <Typography
          variant="caption"
          sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.4 }}
        >
          {netFinal}{' '}
          <Box component="span" sx={{ color: 'text.disabled' }}>{vatNetLabel}</Box>
        </Typography>
      </Box>
    );
  }

  // ── Single-mode (gross or net) ─────────────────────────────────────────────
  const view = priceView.mode === 'gross' ? priceView.gross : priceView.net;
  const isGross = priceView.mode === 'gross';
  const { showSale } = view;
  const finalPrice = formatNumber(view.salePrice ?? view.price);

  return (
    <Box sx={{ width: '100%', textAlign }}>
      {showSale && (
        <Typography
          variant="body2"
          sx={{ color: 'error.main', textDecoration: 'line-through', lineHeight: 1.3 }}
        >
          {formatNumber(view.price)}
        </Typography>
      )}
      <Typography
        variant="h6"
        sx={{
          lineHeight: 1.2,
          fontWeight: 700,
          // Keep vertical alignment consistent when no strikethrough price shown
          ...(!showSale && { mt: 0 }),
        }}
      >
        {finalPrice}
      </Typography>
      {isGross && (
        <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block' }}>
          {vatGrossLabel}
        </Typography>
      )}
      {!isGross && (showVatNotice || true) && (
        <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block' }}>
          {vatNoticeShort}
        </Typography>
      )}
    </Box>
  );
}

// ─── ChooseTicketCategory ─────────────────────────────────────────────────────

export default function ChooseTicketCategory({
  details,
  setNextButtonActive,
  values,
  oneTicketOnly = false,
}) {
  const { t } = useTranslation('checkout');
  const form = useForm();
  const showVatNotice = details?.event?.priceModel === 'net';
  const effectivePriceDisplayMode = resolvePriceDisplayMode(
    details?.event?.frontendSettings?.priceDisplayMode,
    details?.event?.priceModel,
  );

  useEffect(() => {
    if (Object.entries(values).find(([key, value]) => key.startsWith('product_') && value > 0)) {
      setNextButtonActive(true);
      return;
    }
    setNextButtonActive(false);
  }, [values]);

  const productHintText = useMemo(() => {
    if (!details?.event?.frontendSettings?.productHintText) return null;
    return details.event.frontendSettings.productHintText.replace(/^<p>|<\/p>$/g, '');
  }, [details?.event?.frontendSettings?.productHintText]);

  const aboveProductHint = useMemo(() => {
    if (!details?.event?.frontendSettings?.aboveProductHint) return null;
    return details.event.frontendSettings.aboveProductHint;
  }, [details?.event?.frontendSettings?.aboveProductHint]);

  const productIds = useMemo(
    () => details.products?.map((product) => product.id) || [],
    [details.products],
  );

  const selectedProductId = useMemo(() => {
    if (!oneTicketOnly) return null;
    return (
      details.products?.find((product) => Number(values?.[`product_${product.id}`]) > 0)?.id || null
    );
  }, [details.products, oneTicketOnly, values]);

  const clearAttendeesForProduct = (productId) => {
    const attendeeKey = `productAttendee_${productId}`;
    const currentAttendees = values?.[attendeeKey] || [];
    if (currentAttendees.length) form.change(attendeeKey, []);
  };

  const setSingleTicketSelection = (targetProductId) => {
    if (!oneTicketOnly) return;
    const isDeselect = selectedProductId === targetProductId;
    productIds.forEach((productId) => {
      const nextQty = !isDeselect && productId === targetProductId ? 1 : 0;
      const fieldName = `product_${productId}`;
      const currentQty = Number(values?.[fieldName] || 0);
      if (currentQty !== nextQty) form.change(fieldName, nextQty);
      if (nextQty === 0 && currentQty > 0) clearAttendeesForProduct(productId);
    });
  };

  const handleRadioKeyDown = (event, product) => {
    if (!oneTicketOnly || !product.isAvailable) return;
    const enabledProducts = (details.products || []).filter(({ isAvailable }) => isAvailable);
    const enabledIndex = enabledProducts.findIndex(({ id }) => id === product.id);
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setSingleTicketSelection(product.id);
      return;
    }
    if (!['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft'].includes(event.key)) return;
    event.preventDefault();
    const direction = event.key === 'ArrowDown' || event.key === 'ArrowRight' ? 1 : -1;
    const nextIndex = enabledIndex + direction;
    if (nextIndex < 0 || nextIndex >= enabledProducts.length) return;
    const nextProduct = enabledProducts[nextIndex];
    const nextElement = document.querySelector(`[data-ticket-radio-id="${nextProduct.id}"]`);
    if (nextElement) nextElement.focus();
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ marginBottom: aboveProductHint ? 1 : 4 }}>
        {oneTicketOnly ? t('steps.chooseTicketSingular') : t('steps.chooseTicket')}
      </Typography>
      {/* ── Early Bird Banner ── */}
      {details?.event?.frontendSettings?.showEarlyBirdBanner && <EarlyBirdBanner />}
      {aboveProductHint && (
        <Box sx={{ mb: 3, color: 'text.disabled' }}>
          <Typography variant="body2" component="div" sx={{ '& p': { margin: 0 } }}>
            <JsxParser
              components={{
                ExternalLink,
                Typography,
                Box,
                Link: ExternalLink,
                a: ExternalLink,
              }}
              jsx={aboveProductHint}
              autoCloseVoidElements
            />
          </Typography>
        </Box>
      )}
      <Box role={oneTicketOnly ? 'radiogroup' : undefined}>
        {details.products?.map((product) => {
          const priceView = getTicketCardPriceView(product, effectivePriceDisplayMode);
          return (
            <div key={product.id}>
              <Grid2
                container
                sx={{
                  my: oneTicketOnly ? 1 : 2,
                  p: oneTicketOnly ? 1.25 : 0,
                  borderRadius: oneTicketOnly ? 1.5 : 0,
                  border: oneTicketOnly ? '2px solid' : 'none',
                  borderColor: oneTicketOnly
                    ? selectedProductId === product.id
                      ? 'primary.main'
                      : 'divider'
                    : 'transparent',
                  bgcolor: oneTicketOnly
                    ? selectedProductId === product.id
                      ? 'action.selected'
                      : 'transparent'
                    : 'transparent',
                  opacity: oneTicketOnly && !product.isAvailable ? 0.6 : 1,
                  cursor: oneTicketOnly && product.isAvailable ? 'pointer' : 'default',
                  transition: 'background-color 120ms, border-color 120ms',
                }}
                role={oneTicketOnly ? 'radio' : undefined}
                aria-checked={oneTicketOnly ? selectedProductId === product.id : undefined}
                aria-disabled={oneTicketOnly ? !product.isAvailable : undefined}
                tabIndex={oneTicketOnly ? (product.isAvailable ? 0 : -1) : undefined}
                data-ticket-radio-id={oneTicketOnly ? product.id : undefined}
                onClick={() => {
                  if (!oneTicketOnly || !product.isAvailable) return;
                  setSingleTicketSelection(product.id);
                }}
                onKeyDown={(event) => handleRadioKeyDown(event, product)}
              >
                {/* ── Left: ticket name + description ── */}
                <Grid2
                  size={{ xs: 12, sm: 8 }}
                  sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' } }}
                >
                  {oneTicketOnly && (
                    <Box
                      sx={{
                        width: 18,
                        height: 18,
                        borderRadius: '50%',
                        border: '2px solid',
                        borderColor:
                          selectedProductId === product.id ? 'primary.main' : 'divider',
                        bgcolor:
                          selectedProductId === product.id ? 'primary.main' : 'transparent',
                        mr: 1,
                        mt: { xs: '3px', sm: 0 },
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {selectedProductId === product.id && (
                        <Check sx={{ color: '#fff', fontSize: 12, fontWeight: 700 }} />
                      )}
                    </Box>
                  )}
                  <Typography variant="h6">
                    {product.name}
                    {product.shortDescription ? (
                      <Typography variant="subtitle2" sx={{ flex: 1, color: '#9e9e9e' }} component="div">
                        <JsxParser
                          components={{
                            ExternalLink,
                            Typography,
                          }}
                          jsx={product.shortDescription}
                          autoCloseVoidElements
                        />
                      </Typography>
                    ) : null}
                  </Typography>
                </Grid2>

                {/* ── Right: price ── */}
                <Grid2
                  size={{ xs: 12, sm: oneTicketOnly ? 4 : 2 }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    ...(oneTicketOnly && { pt: { xs: 4, sm: 0 } }),
                  }}
                >
                  <PriceDisplay
                    priceView={priceView}
                    showVatNotice={showVatNotice}
                    vatNoticeShort={t('vatNoticeShort')}
                    vatGrossLabel={t('vatGrossLabel')}
                    vatNetLabel={t('vatNetLabel')}
                    textAlign="right"
                  />
                  {oneTicketOnly && !product.isAvailable && (
                    <Typography sx={{ color: '#d32f2f', marginTop: 0.5 }}>
                      {t('soldOut')}
                    </Typography>
                  )}
                </Grid2>

                {/* ── Qty stepper (multi-ticket mode only) ── */}
                {!oneTicketOnly && (
                  <Grid2 size={{ xs: 7, sm: 2 }}>
                    <Field name={`product_${product.id}`}>
                      {({ input }) => (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            height: '100%',
                          }}
                        >
                          {product.isAvailable ? (
                            <>
                              <Button
                                variant="outlined"
                                size="small"
                                sx={{
                                  width: { xs: '40px', sm: '28px' },
                                  minWidth: 0,
                                  height: { xs: '40px', sm: '28px' },
                                  marginRight: 1,
                                }}
                                disabled={input.value === 0}
                                onClick={() => {
                                  if (input.value === 0) return;
                                  const newQty = input.value - 1;
                                  input.onChange(newQty);
                                  const attendeeKey = `productAttendee_${product.id}`;
                                  const currentAttendees = values?.[attendeeKey] || [];
                                  if (currentAttendees.length > newQty) {
                                    form.change(attendeeKey, currentAttendees.slice(0, newQty));
                                  }
                                }}
                              >
                                <Remove />
                              </Button>
                              <Typography sx={{ fontSize: '20px', minWidth: 24, textAlign: 'center' }}>
                                {input.value}
                              </Typography>
                              <Button
                                variant="contained"
                                size="small"
                                sx={{
                                  width: { xs: '40px', sm: '28px' },
                                  minWidth: 0,
                                  height: { xs: '40px', sm: '28px' },
                                  marginLeft: 1,
                                }}
                                disabled={product.maxQuantity <= input.value}
                                onClick={() => {
                                  input.onChange(input.value + 1);
                                }}
                              >
                                <Add />
                              </Button>
                            </>
                          ) : (
                            <Typography sx={{ color: '#d32f2f' }}>{t('soldOut')}</Typography>
                          )}
                        </Box>
                      )}
                    </Field>
                  </Grid2>
                )}
              </Grid2>
              <Divider />
            </div>
          );
        })}
      </Box>
      {productHintText && (
        <Box
          sx={{ mt: 2, display: 'flex', alignItems: 'flex-start', gap: 0.75, color: 'text.disabled' }}
        >
          <Typography variant="caption" sx={{ '& p': { margin: 0 } }} component="div">
            <JsxParser jsx={productHintText} autoCloseVoidElements />
          </Typography>
        </Box>
      )}
    </Box>
  );
}
